import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import {
  Plus,
  FileCheck,
  ChevronRight,
  ChevronDown,
  CalendarIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { FormDrawer } from "@/components/ui/form-drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";

import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

interface Account {
  id: string;
  name: string;
  classification?: string;
}

interface SalesStage {
  id: string;
  name: string;
  displayOrder: number;
  defaultProbability: number;
  color?: string | null;
}

interface Opportunity {
  id: string;
  accountId: string | null;
  categoryId: string | null;
  name: string;
  value: string | null;
  stage: string;
  probability: number;
  expectedCloseDate: string | null;
  notes: string | null;
  createdAt: string;
  account?: Account;
}

interface OpportunitiesResponse {
  opportunities: Opportunity[];
  total: number;
}

interface AccountsResponse {
  accounts: Account[];
  total: number;
}

interface LeadsResponse {
  leads: Account[];
  total: number;
}

const opportunityFormSchema = z.object({
  accountId: z.string().optional(),
  name: z.string().min(1, "Opportunity name is required"),
  value: z.string().min(1, "Estimated value is required"),
  stage: z.string().min(1, "Stage is required"),
  probability: z.coerce.number().int().min(0).max(100),
  expectedCloseDate: z.date().optional(),
  notes: z.string().optional(),
});

type OpportunityFormData = z.infer<typeof opportunityFormSchema>;

function StageGroup({
  groupKey,
  label,
  count,
  totalValue,
  avgProbability,
  isExpanded,
  onToggle,
  opportunities,
  onRowClick,
}: {
  groupKey: string;
  label: string;
  count: number;
  totalValue: number;
  avgProbability: number;
  isExpanded: boolean;
  onToggle: () => void;
  opportunities: Opportunity[];
  onRowClick: (row: Opportunity) => void;
}) {
  return (
    <>
      <TableRow
        className="cursor-pointer bg-muted/50 border-b border-t border-border/50 hover:bg-muted/70"
        onClick={onToggle}
        data-testid={`group-header-${groupKey}`}
      >
        <TableCell colSpan={6}>
          <div className="flex items-center gap-2 py-0.5">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-semibold text-sm">{label}</span>
            <Badge variant="secondary">{count}</Badge>
            <Badge variant="outline" className="text-xs">
              {avgProbability}% avg
            </Badge>
            <span className="text-xs text-muted-foreground ml-auto font-medium">
              ${totalValue.toLocaleString()}
            </span>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded &&
        opportunities.map((row) => {
          const isLead = row.account?.classification === "prospect";
          return (
            <TableRow
              key={row.id}
              onClick={() => onRowClick(row)}
              className="cursor-pointer hover:bg-muted/30"
              data-testid={`table-row-${row.id}`}
            >
              <TableCell>
                <div className="font-medium">{row.name}</div>
              </TableCell>
              <TableCell>
                {row.account ? (
                  <div className="flex items-center gap-2">
                    <span>{row.account.name}</span>
                    {isLead && (
                      <Badge variant="secondary" className="text-xs">
                        Lead
                      </Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>
                <StageBadge stage={row.stage} />
              </TableCell>
              <TableCell className="text-right">
                <span className="font-medium">
                  ${Number(row.value || 0).toLocaleString()}
                </span>
              </TableCell>
              <TableCell className="text-right">{row.probability || 0}%</TableCell>
              <TableCell>
                {row.expectedCloseDate
                  ? format(new Date(row.expectedCloseDate), "MMM d, yyyy")
                  : "—"}
              </TableCell>
            </TableRow>
          );
        })}
    </>
  );
}

export function StageBadge({ stage }: { stage: string }) {
  const lower = stage.toLowerCase().replace(/\s+/g, "_");
  let cls = "bg-gray-500 text-white hover:bg-gray-500";
  if (lower === "closed_won") cls = "bg-green-600 text-white hover:bg-green-600";
  else if (lower === "closed_lost") cls = "bg-red-600 text-white hover:bg-red-600";
  else if (lower === "negotiation") cls = "bg-amber-600 text-white hover:bg-amber-600";
  else if (lower === "proposal") cls = "bg-blue-600 text-white hover:bg-blue-600";
  else if (lower === "qualification") cls = "bg-purple-600 text-white hover:bg-purple-600";
  return (
    <Badge className={cls} variant="default">
      {stage}
    </Badge>
  );
}

export default function OpportunitiesPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<OpportunitiesResponse>({
    queryKey: ["/api/opportunities", { search }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      const qs = params.toString();
      return api.get<OpportunitiesResponse>(
        `/opportunities${qs ? `?${qs}` : ""}`,
        sessionId,
      );
    },
  });

  const { data: salesStages = [] } = useQuery<SalesStage[]>({
    queryKey: ["/api/sales-stages"],
    queryFn: () => api.get<SalesStage[]>("/sales-stages", sessionId),
  });

  const { data: accountsData } = useQuery<AccountsResponse>({
    queryKey: ["/api/accounts", "for-opportunities"],
    queryFn: () => api.get<AccountsResponse>("/accounts?limit=500", sessionId),
    enabled: drawerOpen,
  });
  const { data: leadsData } = useQuery<LeadsResponse>({
    queryKey: ["/api/leads", "for-opportunities"],
    queryFn: () => api.get<LeadsResponse>("/leads?limit=500", sessionId),
    enabled: drawerOpen,
  });

  const opportunities = data?.opportunities || [];

  useEffect(() => {
    if (salesStages.length > 0 && Object.keys(expandedGroups).length === 0) {
      const initial: Record<string, boolean> = {};
      for (const stage of salesStages) {
        const lower = stage.name.toLowerCase().replace(/\s+/g, "_");
        initial[stage.name] = lower !== "closed_won" && lower !== "closed_lost";
      }
      setExpandedGroups(initial);
    }
  }, [salesStages, expandedGroups]);

  const grouped = useMemo(() => {
    const sortedStages = [...salesStages].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    );
    return sortedStages.map((stage) => ({
      stage,
      opportunities: opportunities.filter((o) => o.stage === stage.name),
    }));
  }, [salesStages, opportunities]);

  const uncategorized = useMemo(() => {
    const stageNames = new Set(salesStages.map((s) => s.name));
    return opportunities.filter((o) => !stageNames.has(o.stage));
  }, [salesStages, opportunities]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const form = useForm<OpportunityFormData>({
    resolver: zodResolver(opportunityFormSchema),
    defaultValues: {
      accountId: "",
      name: "",
      value: "",
      stage: "Prospecting",
      probability: 10,
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: OpportunityFormData) => {
      return api.post<Opportunity>(
        "/opportunities",
        {
          accountId: data.accountId || undefined,
          name: data.name,
          value: data.value,
          stage: data.stage,
          probability: data.probability,
          expectedCloseDate: data.expectedCloseDate
            ? data.expectedCloseDate.toISOString()
            : null,
          notes: data.notes || null,
        },
        sessionId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/opportunities"] });
      toast({
        title: "Opportunity created",
        description: "The opportunity has been created successfully.",
      });
      setDrawerOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create opportunity",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: OpportunityFormData) => {
    createMutation.mutate(data);
  };

  const handleStageChange = (stageName: string) => {
    form.setValue("stage", stageName);
    const stage = salesStages.find((s) => s.name === stageName);
    if (stage) form.setValue("probability", stage.defaultProbability);
  };

  const accountOptions = [
    ...(accountsData?.accounts || []),
    ...(leadsData?.leads || []),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Opportunities"
        description="Track sales opportunities through your pipeline"
        actions={
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search opportunities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-64"
            />
            <Button
              onClick={() => {
                form.reset({
                  accountId: "",
                  name: "",
                  value: "",
                  stage: "Prospecting",
                  probability: 10,
                  notes: "",
                });
                setDrawerOpen(true);
              }}
              data-testid="button-new-opportunity"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Opportunity
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {[
                    "Opportunity",
                    "Account / Lead",
                    "Stage",
                    "Value",
                    "Probability",
                    "Expected Close",
                  ].map((h) => (
                    <TableHead key={h}>
                      <Skeleton className="h-4 w-20" />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full max-w-[150px]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : opportunities.length === 0 ? (
        <div className="rounded-md border">
          <div className="flex flex-col items-center justify-center gap-2 text-muted-foreground py-12">
            <FileCheck className="h-12 w-12 text-muted-foreground/50" />
            <p>No opportunities found</p>
          </div>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium uppercase tracking-wide">
                  Opportunity
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">
                  Account / Lead
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">
                  Stage
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                  Value
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                  Probability
                </TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-wide">
                  Expected Close
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map(({ stage, opportunities: stageOpps }) => {
                if (stageOpps.length === 0) return null;
                const isExpanded = expandedGroups[stage.name] ?? true;
                const totalValue = stageOpps.reduce(
                  (sum, o) => sum + Number(o.value || 0),
                  0,
                );
                const avgProb = Math.round(
                  stageOpps.reduce((sum, o) => sum + (o.probability || 0), 0) /
                    stageOpps.length,
                );

                return (
                  <StageGroup
                    key={stage.id}
                    groupKey={stage.name}
                    label={stage.name}
                    count={stageOpps.length}
                    totalValue={totalValue}
                    avgProbability={avgProb}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(stage.name)}
                    opportunities={stageOpps}
                    onRowClick={(row) => setLocation(`/opportunities/${row.id}`)}
                  />
                );
              })}
              {uncategorized.length > 0 && (
                <StageGroup
                  groupKey="__uncategorized"
                  label="Other"
                  count={uncategorized.length}
                  totalValue={uncategorized.reduce(
                    (sum, o) => sum + Number(o.value || 0),
                    0,
                  )}
                  avgProbability={Math.round(
                    uncategorized.reduce(
                      (sum, o) => sum + (o.probability || 0),
                      0,
                    ) / Math.max(1, uncategorized.length),
                  )}
                  isExpanded={expandedGroups["__uncategorized"] ?? true}
                  onToggle={() => toggleGroup("__uncategorized")}
                  opportunities={uncategorized}
                  onRowClick={(row) => setLocation(`/opportunities/${row.id}`)}
                />
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {opportunities.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {opportunities.length} opportunit
          {opportunities.length !== 1 ? "ies" : "y"} total
        </div>
      )}

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="New Opportunity"
        description="Create a new sales opportunity."
        formId="create-opportunity"
        isPending={createMutation.isPending}
        submitLabel="Create Opportunity"
        size="lg"
        data-testid="drawer-new-opportunity"
      >
        <Form {...form}>
          <form
            id="create-opportunity"
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account / Lead</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ""}
                  >
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select account or lead (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accountOptions.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                          {a.classification === "prospect" && " (Lead)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Opportunity Name *</FormLabel>
                  <FormControl>
                    <Input
                      variant="flat"
                      placeholder="Q1 2026 Packaging Refresh"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estimated Value *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          $
                        </span>
                        <Input
                          variant="flat"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="50000"
                          className="pl-7"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="expectedCloseDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Expected Close Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground",
                            )}
                          >
                            {field.value
                              ? format(field.value, "MMM d, yyyy")
                              : "Pick a date"}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0"
                        align="start"
                      >
                        <CalendarComponent
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="stage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stage *</FormLabel>
                    <Select
                      onValueChange={(v) => {
                        field.onChange(v);
                        handleStageChange(v);
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger variant="flat">
                          <SelectValue placeholder="Select stage" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {salesStages.map((s) => (
                          <SelectItem key={s.id} value={s.name}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="probability"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Probability (%)</FormLabel>
                    <FormControl>
                      <Input
                        variant="flat"
                        type="number"
                        min="0"
                        max="100"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea variant="flat" placeholder="Additional details..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
